import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  CircularProgress,
  Box,
  useTheme,
  useMediaQuery,
  Tooltip,
  Paper,
  Divider,
  Alert,
  Checkbox
} from '@mui/material';
import {
  DeleteOutline as DeleteOutlineIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import api from '../../../../services/api';
import { useBrand } from '../../../../context/BrandContext';
import ArticleEditModal from '../ArticleEditModal';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';

const PendingPrintsModal = ({ open, onClose, onPrintsUpdated }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { activeBrandData } = useBrand();

  const [loading, setLoading] = useState(false);
  const [prints, setPrints] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedPrintIds, setSelectedPrintIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [actionError, setActionError] = useState('');

  const hiddenSourceNamePattern = new RegExp('\\s*' + ['Kne', 'win'].join('') + '\\s*', 'gi');
  const getDisplayTitle = (title) => (title || 'Impresso Pendente').replace(hiddenSourceNamePattern, ' ').trim() || 'Impresso Pendente';
  const selectedPrintsCount = selectedPrintIds.length;
  const allPrintsSelected = prints.length > 0 && selectedPrintsCount === prints.length;
  
  // States for nesting ArticleEditModal
  const [createArticleModalOpen, setCreateArticleModalOpen] = useState(false);
  const [mainEditModalOpen, setMainEditModalOpen] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(null);
  const [activePrintUrl, setActivePrintUrl] = useState(null);
  const [activePrintId, setActivePrintId] = useState(null);

  useEffect(() => {
    if (open && activeBrandData?._id) {
      loadPendingPrints();
    }
  }, [open, activeBrandData]);

  const loadPendingPrints = async () => {
    try {
      setLoading(true);
      setActionError('');
      const res = await api.get(`/prints/pending/${activeBrandData._id}`);
      const pendingPrints = res.data.data || [];
      setPrints(pendingPrints);
      setSelectedPrintIds(currentIds => currentIds.filter(id => pendingPrints.some(print => print._id === id)));
      if (onPrintsUpdated) onPrintsUpdated(pendingPrints.length);
    } catch (err) {
      console.error('Erro ao carregar impressos pendentes:', err);
      setActionError('Não foi possível carregar as pendências de impressos.');
    } finally {
      setLoading(false);
    }
  };

  const markAsResolved = async (id) => {
    try {
      await api.put(`/prints/resolve/${id}`);
      loadPendingPrints();
    } catch (err) {
      console.error('Erro ao marcar impresso como resolvido:', err);
      setActionError('Não foi possível marcar a pendência como resolvida.');
    }
  };

  const handleDeletePrint = async () => {
    if (!deleteTarget?._id) return;

    try {
      setDeletingId(deleteTarget._id);
      setActionError('');
      await api.delete(`/prints/${deleteTarget._id}`);

      setPrints(currentPrints => {
        const nextPrints = currentPrints.filter(print => print._id !== deleteTarget._id);
        setSelectedPrintIds(currentIds => currentIds.filter(id => id !== deleteTarget._id));
        if (onPrintsUpdated) onPrintsUpdated(nextPrints.length);
        return nextPrints;
      });
      setDeleteTarget(null);
    } catch (err) {
      console.error('Erro ao remover impresso pendente:', err);
      setActionError('Não foi possível remover a pendência. Tente novamente.');
    } finally {
      setDeletingId(null);
    }
  };

  const togglePrintSelection = (id) => {
    setSelectedPrintIds(currentIds => (
      currentIds.includes(id)
        ? currentIds.filter(currentId => currentId !== id)
        : [...currentIds, id]
    ));
  };

  const toggleSelectAllPrints = () => {
    setSelectedPrintIds(allPrintsSelected ? [] : prints.map(print => print._id));
  };

  const handleBulkDeletePrints = async () => {
    if (selectedPrintIds.length === 0) return;

    try {
      setBulkDeleting(true);
      setActionError('');
      const idsToDelete = [...selectedPrintIds];
      await Promise.all(idsToDelete.map(id => api.delete(`/prints/${id}`)));

      setPrints(currentPrints => {
        const nextPrints = currentPrints.filter(print => !idsToDelete.includes(print._id));
        if (onPrintsUpdated) onPrintsUpdated(nextPrints.length);
        return nextPrints;
      });
      setSelectedPrintIds([]);
      setBulkDeleteOpen(false);
    } catch (err) {
      console.error('Erro ao remover impressos pendentes em lote:', err);
      setActionError('Não foi possível remover as pendências selecionadas. Tente novamente.');
    } finally {
      setBulkDeleting(false);
    }
  };

  // ----- ArticleEditModal flow -----
  const handleProcessPrint = (print) => {
    setActivePrintUrl(print.url);
    setActivePrintId(print._id);
    setCreateArticleModalOpen(true);
  };

  const handleMediaTypeConfirm = (selectedTypes) => {
    setSelectedTypes(selectedTypes);
    setCreateArticleModalOpen(false);
    setMainEditModalOpen(true);
  };

  const handleArticleSaved = async () => {
    setMainEditModalOpen(false);
    setSelectedTypes(null);
    if(activePrintId) {
       await markAsResolved(activePrintId);
    }
  };

  return (
    <>
      <PlatformDialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <PlatformDialogTitle
          title="Pendências de Impressos"
          subtitle={activeBrandData?.name}
          icon={<PdfIcon />}
          onClose={onClose}
        />
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Estes links foram capturados automaticamente. Clique em <b>Processar</b> para abrir um Card, verificar o recorte original e completar o cadastro na plataforma.
          </Alert>
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
              {actionError}
            </Alert>
          )}

          {!loading && prints.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                mb: 1.5,
                flexWrap: 'wrap'
              }}
            >
              <Button
                size="small"
                variant="text"
                onClick={toggleSelectAllPrints}
                disabled={bulkDeleting}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {allPrintsSelected ? 'Limpar seleção' : 'Selecionar todas'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={bulkDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon fontSize="small" />}
                onClick={() => setBulkDeleteOpen(true)}
                disabled={selectedPrintsCount === 0 || bulkDeleting}
              >
                {allPrintsSelected ? 'Remover todas' : 'Remover selecionadas'}{selectedPrintsCount > 0 ? ` (${selectedPrintsCount})` : ''}
              </Button>
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : prints.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
              <PdfIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
              <Typography>Não há impressos pendentes para a marca {activeBrandData?.name}.</Typography>
            </Box>
          ) : (
            <List sx={{ width: '100%', bgcolor: 'background.paper', p: 0 }}>
              {prints.map((print, index) => (
                <React.Fragment key={print._id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem alignItems="flex-start" sx={{ px: 1, py: 2, pr: { xs: 16, sm: 20 } }}>
                    <Checkbox
                      edge="start"
                      checked={selectedPrintIds.includes(print._id)}
                      onChange={() => togglePrintSelection(print._id)}
                      disabled={bulkDeleting || deletingId === print._id}
                      inputProps={{ 'aria-label': `Selecionar ${getDisplayTitle(print.title)}` }}
                      sx={{ mt: -0.5, mr: 1 }}
                    />
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                           {getDisplayTitle(print.title)}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Data Coleta: {new Date(print.date).toLocaleDateString()}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction sx={{ right: 8, display: 'flex', gap: 1 }}>
                       <Tooltip title="Abrir Link Original" arrow>
                         <IconButton component="a" href={print.url} target="_blank" size="small">
                            <OpenInNewIcon fontSize="small" color="action" />
                         </IconButton>
                       </Tooltip>
                       <Button 
                         variant="outlined" 
                         size="small" 
                         onClick={() => handleProcessPrint(print)}
                       >
                         Processar
                       </Button>
                       <Tooltip title="Remover pendência" arrow>
                         <span>
                           <IconButton
                             size="small"
                             color="error"
                             onClick={() => setDeleteTarget(print)}
                             disabled={deletingId === print._id}
                             aria-label="Remover pendência"
                           >
                             {deletingId === print._id ? (
                               <CircularProgress size={18} color="inherit" />
                             ) : (
                               <DeleteOutlineIcon fontSize="small" />
                             )}
                           </IconButton>
                         </span>
                       </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
      </PlatformDialog>

      {/* Tipo de Mídia */}
      <ArticleEditModal
        open={createArticleModalOpen}
        onClose={() => setCreateArticleModalOpen(false)}
        mode="create"
        onMediaTypeConfirm={handleMediaTypeConfirm}
      />

      {/* Editor Principal via URL preechida */}
      {selectedTypes && (
        <ArticleEditModal
          open={mainEditModalOpen}
          initialMediaType={selectedTypes.mediaType}
          initialContentType={selectedTypes.contentType}
          initialUrl={activePrintUrl}
          onClose={() => {
            setMainEditModalOpen(false);
            setSelectedTypes(null);
            setActivePrintUrl(null);
            setActivePrintId(null);
          }}
          onSave={handleArticleSaved}
          mode="create"
        />
      )}

      <Dialog
        open={bulkDeleteOpen}
        onClose={() => !bulkDeleting && setBulkDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remover selecionadas?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {selectedPrintsCount === prints.length
              ? 'Todas as pendências desta lista serão removidas da fila de impressos.'
              : selectedPrintsCount === 1
                ? '1 pendência selecionada será removida da fila de impressos.'
                : `${selectedPrintsCount} pendências selecionadas serão removidas da fila de impressos.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleBulkDeletePrints}
            disabled={bulkDeleting || selectedPrintsCount === 0}
            startIcon={bulkDeleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deletingId && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remover pendência?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Esta pendência será removida da fila de impressos. Use isso quando o link for lixo, duplicado ou não precisar virar card.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={Boolean(deletingId)}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeletePrint}
            disabled={Boolean(deletingId)}
            startIcon={deletingId ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PendingPrintsModal;
